import Hls from 'hls.js';

// Minimal attach/detach for the shorts player. VideoStreamingPlayer
// (routes/videostreaming/player.js) is 1245 lines built around the article
// view — the swipe feed needs direct play/pause control on intersection and
// none of that chrome, so it uses this instead.
export function attachHls(videoEl, url, { onFatal = () => {} } = {}) {
    if (!videoEl || !url) {
        return { destroy: () => {} };
    }

    // Safari plays HLS natively and must NOT get an hls.js instance.
    if (!Hls.isSupported()) {
        if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
            videoEl.src = url;
        }
        return {
            destroy: () => {
                videoEl.removeAttribute('src');
                videoEl.load();
            },
        };
    }

    // maxBufferLength/capLevelToPlayerSize are deliberately tight — the article
    // player (player.js loadHls) tunes for one long-lived DVR/live stream and
    // sets maxBufferLength: 1200 (20 min) plus a 600MB maxBufferSize because a
    // viewer scrubs back through a single ongoing broadcast. The shorts feed is
    // the opposite: many short VOD clips are attached/destroyed in quick
    // succession while swiping, several of them off-screen in the preload
    // window (see lib/shorts.js HLS_WINDOW_RADIUS). A small buffer keeps memory
    // and network use low across that many concurrent instances, and capping
    // the rendition to the (small, portrait, full-bleed) player element avoids
    // fetching quality levels the viewport can never show.
    const hls = new Hls({ maxBufferLength: 10, capLevelToPlayerSize: true });
    hls.loadSource(url);
    hls.attachMedia(videoEl);

    hls.on(Hls.Events.ERROR, (event, data) => {
        // Non-fatal errors are routine (a segment retry, a stalled fetch that
        // recovers on its own) — hls.js handles those internally. Only FATAL
        // errors get the recovery ladder below.
        if (!data.fatal) {
            return;
        }
        switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
                hls.startLoad();
                break;
            case Hls.ErrorTypes.MEDIA_ERROR:
                hls.recoverMediaError();
                break;
            default:
                hls.destroy();
                // No auto-advance: silently skipping a broken clip looks exactly
                // like one that ended, and hides the fault from viewer and author.
                onFatal(data);
                break;
        }
    });

    return {
        destroy: () => {
            try {
                hls.destroy();
            } catch (err) {
                // already torn down — safe to call destroy() more than once.
            }
        },
    };
}

export default { attachHls };
